<template>
  <div id="app" class="terminal">
    <header v-if="$route.path !== '/admin'">
      <div class="terminal-header">
        <span class="prompt">$</span>
        <span class="command">zen-corporation</span>
        <span class="version">v1.0.0</span>
      </div>
    </header>
    <router-view v-if="$route.path === '/admin'" class="admin-route" />
    <section v-else class="content">
      <div class="typing-container">
        <span class="typing-text">{{ displayText }}</span>
        <span v-if="!isTypingComplete" class="typing-cursor">|</span>
      </div>
      
      <!-- メインメニューとセクション -->
      <div v-if="isTypingComplete" class="content-container fade-in" :class="{ 'has-active-menu': selectedMenu }">
        <!-- Company Overview セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'Company Overview' }" @click="selectMenu('Company Overview')" style="--menu-index: 0;">
          <span class="menu-prompt">> </span>Company Overview
        </div>
        <div v-if="selectedMenu === 'Company Overview'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            Company Overview
          </div>
          <p class="typing-content">{{ aboutText }}<span v-if="typingState.about" class="typing-cursor">|</span></p>
        </div>

        <!-- Corporate Philosophy & Vision セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'Corporate Philosophy & Vision' }" @click="selectMenu('Corporate Philosophy & Vision')" style="--menu-index: 1;">
          <span class="menu-prompt">> </span>Corporate Philosophy & Vision
        </div>
        <div v-if="selectedMenu === 'Corporate Philosophy & Vision'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            Corporate Philosophy & Vision
          </div>
          <p class="typing-content">{{ philosophyText }}<span v-if="typingState.philosophy" class="dot-cursor"></span></p>
        </div>

        <!-- Business Overview & Service Offerings セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'Business Overview & Service Offerings' }" @click="selectMenu('Business Overview & Service Offerings')" style="--menu-index: 2;">
          <span class="menu-prompt">> </span>Business Overview & Service Offerings
        </div>
        <div v-if="selectedMenu === 'Business Overview & Service Offerings'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            Business Overview & Service Offerings
          </div>
          <div class="service-list">
            <div v-if="displayedServices.length === 0" class="empty-state"><span class="dot-cursor"></span></div>
            <div v-for="(service, index) in displayedServices" :key="index" class="service-category">
              <h3 class="service-title">{{ service.title }}</h3>
              <ul>
                <li v-for="(item, itemIndex) in service.items" :key="itemIndex">
                  {{ item }}
                </li>
              </ul>
              <span v-if="typingState.services && index === displayedServices.length - 1" class="dot-cursor"></span>
            </div>
          </div>
        </div>

        <!-- Management & Organizational Structure セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'Management & Organizational Structure' }" @click="selectMenu('Management & Organizational Structure')" style="--menu-index: 3;">
          <span class="menu-prompt">> </span>Management & Organizational Structure
        </div>
        <div v-if="selectedMenu === 'Management & Organizational Structure'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            Management & Organizational Structure
          </div>
          <p class="typing-content">{{ managementText }}<span v-if="typingState.management" class="dot-cursor"></span></p>
        </div>

        <!-- News & Press Releases セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'News & Press Releases' }" @click="selectMenu('News & Press Releases')" style="--menu-index: 4;">
          <span class="menu-prompt">> </span>News & Press Releases
        </div>
        <div v-if="selectedMenu === 'News & Press Releases'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            News & Press Releases
          </div>
          <ul class="news-list">
            <li v-if="displayedNews.length === 0" class="empty-state"><span class="dot-cursor"></span></li>
            <li v-for="(news, index) in displayedNews" :key="index">{{ news }}<span v-if="typingState.news && index === displayedNews.length - 1" class="dot-cursor"></span></li>
          </ul>
        </div>

        <!-- Contact & Access Information セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'Contact & Access Information' }" @click="selectMenu('Contact & Access Information')" style="--menu-index: 5;">
          <span class="menu-prompt">> </span>Contact & Access Information
        </div>
        <div v-if="selectedMenu === 'Contact & Access Information'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            Contact & Access Information
          </div>
          <p class="typing-content contact-text">
            <span v-if="!contactText" class="dot-cursor"></span>
            {{ contactText }}<span v-if="typingState.contact" class="dot-cursor"></span>
          </p>
          <ContactForm />
        </div>

        <!-- Privacy Policy & Terms of Use セクション -->
        <div class="menu-item" :class="{ active: selectedMenu === 'Privacy Policy & Terms of Use' }" @click="selectMenu('Privacy Policy & Terms of Use')" style="--menu-index: 6;">
          <span class="menu-prompt">> </span>Privacy Policy & Terms of Use
        </div>
        <div v-if="selectedMenu === 'Privacy Policy & Terms of Use'" class="portfolio-item slide-in">
          <div class="info-box">
            <span class="close-button" @click="closeMenu">&times;</span>
            プライバシーポリシー・利用規約
          </div>
          <p class="typing-content">{{ privacyText }}<span v-if="typingState.privacy" class="dot-cursor"></span></p>
        </div>
      </div>
    </section>
    <div v-if="isTypingComplete && !typingState.about && !typingState.services && !typingState.contact" class="cursor">_</div>
  </div>
</template>

<script>
import ContactForm from './components/ContactForm.vue'

export default {
  name: 'App',
  components: {
    ContactForm
  },
  data() {
    return {
      fullText: 'Welcome to ZEN LLC. Technology & Innovation',
      displayText: '',
      typingSpeed: 50,
      isTypingComplete: false,
      selectedMenu: null,
      menus: [
        'Company Overview',
        'Corporate Philosophy & Vision',
        'Business Overview & Service Offerings',
        'Management & Organizational Structure',
        'News & Press Releases',
        'Contact & Access Information',
        'Privacy Policy & Terms of Use'
      ],
      displayedMenus: [],
      aboutText: '',
      philosophyText: '',
      managementText: '',
      privacyText: '',
      privacyFullText: `プライバシーポリシー・利用規約
プライバシーポリシー
合同会社ZEN（以下「当社」）は、本サイトを通じ取得する利用者の個人情報保護に努め、以下の方針に基づき適切に取り扱います。

個人情報の取得と利用目的

取得方法:
お問い合わせフォーム、ニュースレター登録、サービス利用などで、氏名、メールアドレス、電話番号等を取得。
利用目的:
サービス提供・改善、お問い合わせ対応、最新情報やキャンペーンの案内、各種連絡に利用。
個人情報の第三者提供
法令に基づく場合を除き、利用者の同意なく第三者へ提供しません。業務委託先等への提供は、必要最小限に留め、適切に管理します。

個人情報の安全管理
漏洩、改ざん、不正アクセスを防ぐため、合理的な安全管理措置を講じます。

クッキー（Cookie）の利用について
利用者の利便性向上のためにクッキーを使用します。ブラウザ設定で拒否可能ですが、その場合一部サービスが正しく利用できない可能性があります。

利用者の権利
利用者は、保有する個人情報の開示、訂正、利用停止等を求める権利があります。ご希望の場合は下記のお問い合わせ先までご連絡ください。

プライバシーポリシーの変更
法令や業務内容の変更に伴い、本方針を変更することがあります。変更後は、本サイト掲載時点より適用されます。

【お問い合わせ先】
合同会社ZEN
メール: [メールアドレス]
電話: [電話番号]
所在地: 兵庫県神戸市中央区磯辺通1丁目1番18号カサベラ国際プラザビル7F

利用規約
本利用規約（以下「本規約」）は、合同会社ZEN（以下「当社」）が運営する本サイトの利用条件を定めるものです。本サイト利用者（以下「利用者」）は、本規約に同意の上、本サイトをご利用ください。

定義

本サイト: 当社が運営するWEBサイトおよび関連オンラインサービス。
利用者: 本サイトを利用する全ての個人及び法人。
利用条件
利用者は、本規約に従い、適法かつ適正な方法で本サイトを利用するものとします。違反があった場合、当社は事前通知なく利用停止等の措置を講じることがあります。

禁止事項
以下の行為を禁止します。

法令または公序良俗に反する行為
他者の権利（知的財産権、プライバシー等）の侵害
本サイトの運営妨害、不正アクセス、ウイルス送信等の情報セキュリティ違反行為
知的財産権
本サイトに掲載される全コンテンツ（文章、画像、動画、ロゴ等）の著作権その他の知的財産権は、当社または正当な権利者に帰属します。利用者は、当社の事前許可なく、これらを複製・転用・頒布してはなりません。

免責事項
当社は、本サイト上の情報の正確性や最新性について十分留意していますが、その完全性や正確性を保証するものではありません。利用者は、本サイトの情報を自己責任で利用するものとし、これに起因する損害について当社は一切の責任を負いません。
また、本サイト利用に関連する利用者間または利用者と第三者間の紛争についても、当社は一切関与しません。

規約の変更
必要に応じ、当社は利用者に事前通知なく本規約を変更する場合があります。変更後の規約は、本サイト掲載時点から効力を発生します。

準拠法および裁判管轄
本規約の解釈および適用は日本法に準拠します。関連紛争は、合同会社ZEN所在地を管轄する裁判所を第一審の専属的合意管轄裁判所とします。`,
      services: [
        {
          title: '1. WEB制作・保守',
          items: [
            'WEBサイトの企画・デザイン・開発：クライアントのビジネスモデルに合わせたオリジナルWEBサイトを構築。ユーザビリティ、SEO対策、レスポンシブデザインを重視し、集客力のあるサイト作りを実現します。',
            'サイト保守・運用サポート：公開後の定期更新、セキュリティ管理、パフォーマンス改善など、安心して運用いただける保守サービスを提供します。'
          ]
        },
        {
          title: '2. コンサルティング',
          items: [
            'WEB戦略の策定支援：現状のWEBサイト評価や市場分析をもとに、効果的なWEB戦略の立案を支援。企業成長に向けた改善提案や新たな取り組みをサポートします。',
            '運用改善アドバイス：データ分析や競合調査に基づいた具体的な改善策を提案し、戦略的な運用を促進します。'
          ]
        },
        {
          title: '3. マーケティング',
          items: [
            'デジタルマーケティング支援：SNS広告、コンテンツマーケティング、メールマーケティングなど多角的な手法で、ブランド認知の向上と集客を実現します。',
            '効果測定とPDCAサイクルの実施：キャンペーンの効果を分析し、改善策を講じることでROIの最大化を目指します。'
          ]
        },
        {
          title: '4. WEBシステム開発・保守',
          items: [
            '業務効率化支援システムの開発：クライアントの業務プロセスやビジネスモデルに合わせたカスタムWEBシステムを開発。自動化や効率向上に貢献します。',
            'システム運用・保守：安定稼働とセキュリティ強化を重視し、定期アップデートやトラブルシューティングを実施します。'
          ]
        },
        {
          title: 'SNS運用代行',
          items: [
            '戦略的SNS運用支援：各種SNSプラットフォームでのプロモーション戦略を立案し、実行します。',
            'コンテンツ作成とコミュニケーション管理：定期的な投稿とエンゲージメント強化により、ブランド認知度と顧客ロイヤルティを向上させます。'
          ]
        }
      ],
      displayedServices: [],
      displayedNews: [],
      contactText: '',
      contacts: ['お問い合わせ：info@zen-llc.com', '所在地：兵庫県神戸市中央区磯辺通1丁目1番18号カサベラ国際プラザビル7F'],
      aboutFullText: `【基本情報】

会社名：合同会社ZEN
設立：令和4年9月22日
所在地：兵庫県神戸市中央区磯辺通1丁目1番18号カサベラ国際プラザビル7F
資本金：50万円

【代表者情報】
代表者：大村 大樹（代表社員）
学歴：兵庫県立須磨東高等学校 卒業

【職歴】
■ 個人事業主として13年間、各種事業に従事

■ サービス業界でのキャリア支援・人材仲介業務（約4年間）
   ・業界特有のニーズを把握し、就業機会拡大に向けた人材マッチングを実施
   ・働く女性とサービス提供企業双方との信頼関係の構築を支援
   ・業界内の情報収集や分析を通じ、最適な就業環境の提案に貢献

■ WEB事業への展開
   ・サービス業界での経験を活かし、WEB広告の導入タイミングを捉えHP制作に着手
   ・HP制作を基盤として、以下のWEB関連サービスを展開
     - WEBコンサルティング
     - マーケティング
     - WEBシステム開発・保守
     - SNS運用代行

【従業員数】
1名`,
      philosophyFullText: `【経営理念】
合同会社ZENは、技術と人間性の融合を通じ、クライアントおよび社会全体の課題解決に挑戦し、革新的なWEBソリューションを提供することで、持続可能な成長と社会発展に貢献することを理念としています。

■ 信頼の構築
クライアント、パートナー、従業員との強固な信頼関係を基盤に、透明性の高いコミュニケーションと誠実な対応を実践します。

■ 革新と挑戦
最新技術と新たなアイデアを積極的に取り入れ、業界をリードする革新的なソリューションを追求します。

■ 持続可能な成長
短期的な成果だけでなく、長期的な視点に立った経営とサービス提供を行い、企業としての持続可能な成長を実現します。

【ビジョン】
合同会社ZENは、未来志向のWEBテクノロジーとサービスを通じ、以下のビジョンを掲げます。

■ 未来志向のイノベーション
最先端の技術を駆使し、クライアントの競争力向上を支援。業界に変革をもたらす革新的なWEBソリューションを提供します。

■ 顧客中心のサービス提供
顧客のニーズに寄り添い、最適な戦略と施策を提案することで、高い顧客満足度と信頼を獲得し、長期的なパートナーシップを構築します。

■ 社会貢献と責任ある経営
地域社会や業界全体の発展に寄与するとともに、環境保護や社会的責任を果たす経営活動を推進します。`,
      managementFullText: `経営陣・組織体制
合同会社ZENは、シンプルで迅速な意思決定を実現するフラットな組織体制を採用しています。

代表社員
大村 大樹
役職: 代表社員
経歴:
兵庫県立須磨東高等学校 卒業
個人事業主として13年間、サービス業界でのキャリア支援・人材仲介業務（約4年間）およびWEB関連事業を展開
主な責任・役割:
経営戦略の策定と企業運営全般の統括
クライアントとの主要窓口としての対応およびパートナーシップの構築
新規事業やサービス展開の企画立案と実行

組織体制
現状:
現在、代表社員である大村 大樹1名により運営されています。シンプルな構造により迅速な意思決定と柔軟な業務遂行が可能です。

外部パートナーとの連携:
プロジェクトごとに専門分野の外部パートナーやフリーランスと連携し、最適なリソースを活用しています。

将来的な拡充:
事業拡大に伴い、適切なタイミングで専門チームの編成や人材採用を進め、組織体制の拡充を検討中です。`,
      news: [
        '2024年3月: AI事業部新設のお知らせ',
        '2024年2月: クラウドサービスの機能拡充について',
        '2024年1月: 新年のご挨拶'
      ],
      typingState: {
        about: false,
        philosophy: false,
        services: false,
        management: false,
        news: false,
        contact: false,
        privacy: false
      }
    }
  },
  mounted() {
    this.typeText()
  },
  methods: {
    typeText() {
      let currentIndex = 0
      const typing = setInterval(() => {
        if (currentIndex < this.fullText.length) {
          this.displayText += this.fullText[currentIndex]
          currentIndex++
        } else {
          clearInterval(typing)
          this.isTypingComplete = true
          this.typeMenus()
        }
      }, this.typingSpeed)
    },
    typeMenus() {
      let currentIndex = 0
      const typing = setInterval(() => {
        if (currentIndex < this.menus.length) {
          this.displayedMenus.push(this.menus[currentIndex])
          currentIndex++
        } else {
          clearInterval(typing)
        }
      }, this.typingSpeed * 10)
    },
    selectMenu(menu) {
      if (this.selectedMenu === menu) {
        this.closeMenu();
        return;
      }
      this.resetContent();
      this.selectedMenu = menu;
      switch(menu) {
        case 'Company Overview':
          this.typeAboutText();
          break;
        case 'Corporate Philosophy & Vision':
          this.typePhilosophyText();
          break;
        case 'Business Overview & Service Offerings':
          this.typeServices();
          break;
        case 'Management & Organizational Structure':
          this.typeManagementText();
          break;
        case 'News & Press Releases':
          this.typeNews();
          break;
        case 'Contact & Access Information':
          this.typeContacts();
          break;
        case 'Privacy Policy & Terms of Use':
          this.typePrivacyText();
          break;
      }
    },
    closeMenu() {
      this.selectedMenu = null;
      Object.keys(this.typingState).forEach(key => {
        this.typingState[key] = false;
      });
    },
    resetContent() {
      this.aboutText = '';
      this.philosophyText = '';
      this.displayedServices = [];
      this.managementText = '';
      this.displayedNews = [];
      this.contactText = '';
      this.privacyText = '';
    },
    typeAboutText() {
      this.typingState.about = true;
      let currentIndex = 0
      const typing = setInterval(() => {
        if (currentIndex < this.aboutFullText.length) {
          this.aboutText += this.aboutFullText[currentIndex]
          currentIndex++
        } else {
          clearInterval(typing)
          this.typingState.about = false;
        }
      }, this.typingSpeed)
    },
    typePhilosophyText() {
      this.typingState.philosophy = true;
      let currentIndex = 0;
      const typing = setInterval(() => {
        if (currentIndex < this.philosophyFullText.length) {
          this.philosophyText += this.philosophyFullText[currentIndex];
          currentIndex++;
        } else {
          clearInterval(typing);
          this.typingState.philosophy = false;
        }
      }, this.typingSpeed);
    },
    typeServices() {
      this.typingState.services = true;
      let currentIndex = 0
      const typing = setInterval(() => {
        if (currentIndex < this.services.length) {
          this.displayedServices.push(this.services[currentIndex])
          currentIndex++
        } else {
          clearInterval(typing)
          this.typingState.services = false;
        }
      }, this.typingSpeed * 20)
    },
    typeManagementText() {
      this.typingState.management = true;
      let currentIndex = 0;
      const typing = setInterval(() => {
        if (currentIndex < this.managementFullText.length) {
          this.managementText += this.managementFullText[currentIndex];
          currentIndex++;
        } else {
          clearInterval(typing);
          this.typingState.management = false;
        }
      }, this.typingSpeed);
    },
    typeNews() {
      this.typingState.news = true;
      let currentIndex = 0;
      const typing = setInterval(() => {
        if (currentIndex < this.news.length) {
          this.displayedNews.push(this.news[currentIndex]);
          currentIndex++;
        } else {
          clearInterval(typing);
          this.typingState.news = false;
        }
      }, this.typingSpeed * 20);
    },
    typeContacts() {
      this.typingState.contact = true;
      let currentIndex = 0;
      const typing = setInterval(() => {
        if (currentIndex < this.contacts.length) {
          if (this.contactText) {
            this.contactText += '\n' + this.contacts[currentIndex];
          } else {
            this.contactText = this.contacts[currentIndex];
          }
          currentIndex++;
        } else {
          clearInterval(typing);
          this.typingState.contact = false;
        }
      }, this.typingSpeed * 20);
    },
    typePrivacyText() {
      this.typingState.privacy = true;
      let currentIndex = 0;
      const typing = setInterval(() => {
        if (currentIndex < this.privacyFullText.length) {
          this.privacyText += this.privacyFullText[currentIndex];
          currentIndex++;
        } else {
          clearInterval(typing);
          this.typingState.privacy = false;
        }
      }, this.typingSpeed);
    }
  }
}
</script>

<style>
/* グローバルスタイル */
html, body {
  margin: 0;
  padding: 0;
  background-color: #000;
  min-height: 100vh;
}
</style>

<style scoped>
.terminal {
  background-color: #000;  /* 黒背景 */
  color: #39FF14;         /* 蛍光黄緑 */
  font-family: 'Courier New', Courier, monospace;
  min-height: 100vh;
  padding: 20px;
}

header h1 {
  font-size: 2.5em;
  margin-bottom: 20px;
  text-align: center;
}

.content {
  margin-top: 20px;
  font-size: 1.2em;
}

.typing-container {
  text-align: center;
  margin: 0 auto;
  min-height: 1.5em;
  max-width: 100%;
  overflow-x: hidden;
}

.typing-text {
  letter-spacing: 2px;
  white-space: nowrap;
}

.typing-cursor {
  display: inline-block;
  color: #39FF14;
  animation: blink 0.75s step-end infinite;
  margin-left: 2px;
}

@keyframes blink {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

.portfolio-grid {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 50px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.portfolio-item {
  text-align: center;
  padding: 20px;
  border: 1px solid #39FF14;
  border-radius: 5px;
  width: 100%;
}

.info-box {
  border: 2px solid #39FF14;
  color: #39FF14;
  padding: 20px 40px;  /* 右側のパディングを増やして閉じるボタン用のスペースを確保 */
  font-size: 1.4em;
  text-align: center;
  margin-bottom: 20px;
  font-weight: bold;
  background-color: rgba(57, 255, 20, 0.1);
  position: relative;
  display: flex;  /* Flexboxを使用 */
  justify-content: center;  /* 中央揃え */
  align-items: center;  /* 垂直方向の中央揃え */
}

.close-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.5em;
  padding: 0 10px;
  transition: color 0.3s ease;
  background-color: rgba(0, 0, 0, 0.7);  /* 背景色を追加して文字との重なりを防ぐ */
  border-radius: 4px;  /* 角を丸くする */
  z-index: 1;  /* 他の要素より前面に表示 */
}

.close-button:hover {
  color: #fff;
}

.service-list {
  padding: 0;
  text-align: left;
  margin: 20px auto;
}

.service-category {
  margin-bottom: 30px;
}

.service-title {
  color: #39FF14;
  font-size: 1.2em;
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(57, 255, 20, 0.3);
}

.service-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-category li {
  margin: 10px 0;
  padding-left: 20px;
  position: relative;
  line-height: 1.6;
}

.service-category li:before {
  content: ">";
  position: absolute;
  left: 0;
  color: #39FF14;
}

.cursor {
  display: inline-block;
  margin-left: 2px;
  border-right: 2px solid #39FF14;
  animation: blink 0.75s step-end infinite;
}

@keyframes blink {
  0%, 100% { border-color: transparent; }
  50% { border-color: #39FF14; }
}

/* レスポンシブ対応は不要になったため削除 */
@media (max-width: 768px) {
  .portfolio-grid {
    max-width: 90%;
  }
}

@media (max-width: 480px) {
  .portfolio-grid {
    max-width: 95%;
  }
  
  header h1 {
    font-size: 2em;
  }
}

.terminal-header {
  font-family: 'Courier New', Courier, monospace;
  padding: 10px;
  border-bottom: 1px solid #39FF14;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.prompt {
  color: #39FF14;
  font-weight: bold;
}

.command {
  color: #39FF14;
  font-size: 1.5em;
}

.version {
  color: rgba(57, 255, 20, 0.7);
  font-size: 0.9em;
  margin-left: auto;
}

.fade-in {
  opacity: 0;
  animation: fadeIn 0.5s ease-out forwards;
}

.slide-in {
  opacity: 0;
  transform: translateY(20px);
  animation: slideIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.menu-item {
  font-size: 1.4em;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  padding: 10px;
  border-radius: 5px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  margin: 0 auto;
}

.content-container.has-active-menu .menu-item {
  margin: 0;
  justify-content: flex-start;
  transform: translateX(0);
  transition-delay: calc(var(--menu-index) * 0.4s);
}

.menu-item.active {
  background-color: rgba(57, 255, 20, 0.1);
}

.menu-item:hover {
  background-color: rgba(57, 255, 20, 0.1);
  transform: translateX(5px);
  transition: all 0.2s ease-in-out;
}

.content-container.has-active-menu .menu-item:hover {
  transform: translateX(0);
}

.portfolio-item {
  margin: 10px 0 20px 0;
  padding: 20px;
  border: 1px solid #39FF14;
  border-radius: 5px;
}

.menu-prompt {
  color: #39FF14;
  font-weight: bold;
  margin-right: 10px;
  display: inline-block;
}

.typing-content {
  white-space: pre-line;
  text-align: left;
  line-height: 1.6;
  margin: 20px 0;
}

.contact-text {
  text-align: center;
}

.dot-cursor {
  display: inline-block;
  margin-left: 2px;
  color: #39FF14;
  animation: dotAnimation 1.5s steps(1) infinite;
}

@keyframes dotAnimation {
  0% { content: '.'; }
  25% { content: '..'; }
  50% { content: '...'; }
  75% { content: '.'; }
  100% { content: '..'; }
}

.dot-cursor::after {
  content: '.';
  animation: dotAnimation 1.5s steps(1) infinite;
}

.news-list {
  list-style: none;
  padding: 0;
  text-align: left;
  margin: 20px auto;
}

.news-list li {
  margin: 10px 0;
  padding-left: 20px;
  position: relative;
}

.news-list li:before {
  content: ">";
  position: absolute;
  left: 0;
  color: #39FF14;
}

.empty-state {
  text-align: center;
  padding-left: 0 !important;
}

.empty-state:before {
  content: none !important;
}

/* レスポンシブ対応 */
@media screen and (max-width: 1200px) {
  .content-container {
    max-width: 90%;
    padding: 0 20px;
  }
}

@media screen and (max-width: 768px) {
  .terminal {
    padding: 10px;
  }

  .content {
    font-size: 1em;
  }

  .menu-item {
    font-size: 1.2em;
    padding: 8px;
    margin: 0;
    justify-content: flex-start;
    width: 100%;
  }

  .menu-prompt {
    margin-left: 10px;
  }

  .info-box {
    font-size: 1.2em;
    padding: 15px;
  }

  .service-title {
    font-size: 1.1em;
  }

  .service-category li {
    font-size: 0.9em;
    line-height: 1.5;
  }

  .typing-content {
    font-size: 0.9em;
    margin: 15px 0;
  }

  .portfolio-item {
    padding: 15px;
  }

  .content-container.has-active-menu .menu-item {
    transform: none;
    transition: none;
  }

  .menu-item:hover {
    transform: none;
  }

  .typing-text {
    white-space: normal;
    word-wrap: break-word;
    display: inline-block;
    max-width: 100%;
  }

  .typing-container {
    padding: 0 15px;
  }
}

@media screen and (max-width: 480px) {
  .terminal-header {
    padding: 8px;
    margin-bottom: 20px;
  }

  .command {
    font-size: 1.2em;
  }

  .version {
    font-size: 0.8em;
  }

  .menu-item {
    font-size: 1em;
    padding: 6px;
  }

  .info-box {
    padding: 12px 35px;  /* モバイル用にパディングを調整 */
    font-size: 1em;
  }

  .service-title {
    font-size: 1em;
  }

  .service-category li {
    font-size: 0.85em;
    padding-left: 15px;
  }

  .typing-content {
    font-size: 0.85em;
    margin: 12px 0;
  }

  .portfolio-item {
    padding: 12px;
    margin: 8px 0 16px 0;
  }

  .close-button {
    font-size: 1.2em;
    padding: 0 8px;
    right: 5px;  /* 右側の余白を少し調整 */
  }
}

@media screen and (max-width: 320px) {
  .terminal {
    padding: 8px;
  }

  .content {
    font-size: 0.9em;
  }

  .menu-item {
    font-size: 0.9em;
    padding: 5px;
  }

  .service-category li {
    font-size: 0.8em;
  }

  .typing-content {
    font-size: 0.8em;
  }

  .info-box {
    padding: 10px 30px;  /* さらに小さい画面用にパディングを調整 */
  }
}

.admin-section {
  margin-top: 40px;
  padding: 20px;
  border-top: 1px solid rgba(57, 255, 20, 0.3);
}

.admin-link {
  color: #39FF14;
  text-decoration: none;
  font-family: 'Courier New', Courier, monospace;
}

.admin-link:hover {
  text-decoration: underline;
}

.admin-route {
  height: 100vh;
  width: 100%;
  background-color: #000;
}
</style>
